import React, { useCallback } from 'react'
import { Box, Tabs, Tab, Stack, Divider, Typography, Button } from '@mui/material'
import { Build, Description, Settings, AutoFixHigh as MagicWand } from '@mui/icons-material'
import SettingsDetails from '../SettingsDetails'
import SpecificationSections from './SpecificationSections'
import ArtifactEditor from './ArtifactEditor'
import { useTranslationMapDetailsContext } from '../../contexts/TranslationMapDetailsContext'
import { useGenerateArtifact } from '../../api'
import { toast } from 'atoms/Toast'

export interface MappingEditorProps {
  disabled?: boolean
}

const MappingEditor = React.memo(function MappingEditor({ disabled = false }: MappingEditorProps) {
  // Get all needed data from context
  const { translationMap, viewMode, mappingEditor, isDirty } = useTranslationMapDetailsContext()

  const { activeTab, setActiveTab } = mappingEditor

  // Generate Artifact API hook
  const { mutate: generateArtifact, isLoading: isGeneratingArtifact } = useGenerateArtifact()

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }, [])

  const handleGenerateArtifact = useCallback(() => {
    if (!translationMap?.id) {
      toast.error('Translation map ID is required to generate artifact')
      return
    }

    generateArtifact(translationMap.id, {
      onSuccess: updatedTranslationMap => {
        toast.success('Artifact generated successfully')

        // Switch to the Artifact tab to show the generated content
        setActiveTab(1)
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to generate artifact'
        toast.error(errorMessage)
      },
    })
  }, [translationMap?.id, generateArtifact, setActiveTab])

  return (
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Enhanced Tabs */}
      <Box
        sx={{
          borderColor: 'divider',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, primary.main, transparent)',
            opacity: 0.3,
          },
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              position: 'relative',
              fontWeight: 600,
              textTransform: 'none',
              minHeight: 55,
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: 'action.hover',
                transform: 'translateY(-1px)',
              },
              '&.Mui-selected': {
                color: 'primary.main',
              },
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: '2px 2px 0 0',
              background: 'linear-gradient(90deg, primary.main, primary.light)',
              boxShadow: '0 2px 8px rgba(51, 204, 96, 0.3)',
            },
          }}
        >
          <Tab
            icon={<Description />}
            label="Specification"
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />

          <Tab
            icon={<Build />}
            label="Artifact"
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />

          {viewMode === 'advanced' && (
            <Tab
              icon={<Settings />}
              label="Settings"
              iconPosition="start"
              sx={{ textTransform: 'none' }}
            />
          )}
        </Tabs>
      </Box>

      <Divider />

      {/* Enhanced Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Specification Tab */}
        {activeTab === 0 && (
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  maxWidth: '60%',
                  lineHeight: 1.6,
                }}
              >
                Define the mapping rules and transformations between your input and output data.
              </Typography>

              {/* Button - Generate Artifact */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<MagicWand />}
                onClick={handleGenerateArtifact}
                disabled={disabled || isGeneratingArtifact}
              >
                {isGeneratingArtifact ? 'Generating...' : 'Generate Artifact'}
              </Button>
            </Box>

            <SpecificationSections disabled={disabled} viewMode={viewMode} />
          </Box>
        )}

        {/* Artifact Tab */}
        {activeTab === 1 && <ArtifactEditor disabled={disabled} />}

        {/* Settings Tab - Only show in advanced view */}
        {viewMode === 'advanced' && activeTab === 2 && (
          <Box
            sx={{
              flex: 1,
              minHeight: '400px',
              p: 2,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, primary.main, primary.light)',
                opacity: 0.6,
                zIndex: 1,
              },
            }}
          >
            <SettingsDetails />
          </Box>
        )}
      </Box>
    </Stack>
  )
})

export default MappingEditor
